import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchivedConversionRateList, unArchiveConversionRate } from '../../slices/conversion-rate';

export default function ArchiveConversionRateList() {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { data = [], totalRecord = 0, status } = useSelector((state) => state.conversionRate || {});
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagesize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState('');

    const columns = [
        {
            title: 'Sr. No.',
            dataIndex: 'SrNo',
            key: 'SrNo',
            width: 100,
            sorter: false,
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer',
            key: 'customer',
            sorter: true,
            render: (customer) => <span>{customer || 'N/A'}</span>,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_, record) => (
                <Space>
                    <Tooltip placement="top" title="Restore">
                        <Popconfirm
                            title="Are you sure you want to Unarchive this record?"
                            onCancel={() => message.info('Action cancelled')}
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => handleRestore(record.id)}
                        >
                            <ArchiveRestore className="ArchiverestorImg" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const handleRestore = (id) => {
        if (!id) {
            message.error('Missing ID for deletion!');
            return;
        }

        dispatch(unArchiveConversionRate(id))
            .unwrap()
            .then(() => {
                message.success('Conversion Rate Unarchived successfully!');
                dispatch(
                    fetchArchivedConversionRateList({
                        page,
                        pagesize,
                        ...(search && { search }),
                        ...(orderBy && { ordering: orderBy }),
                    })
                );
            })
            .catch((err) => {
                message.error(`Error: ${err.message || 'Failed to unarchive the record.'}`);
            });
    };

    useEffect(() => {
        dispatch(
            fetchArchivedConversionRateList({
                page,
                pagesize,
                ...(search && { search }),
                ...(orderBy && { ordering: orderBy }),
            })
        );
    }, [dispatch, page, pagesize, search, orderBy]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    // Filter duplicates using a Set based on the 'id' field
    const uniqueData = Array.from(
        new Set(data.map((item) => item.id)),
        (id) => data.find((item) => item.id === id)
    );

    const dataSource = useMemo(() => {
        const seenCustomers = new Set();
        return data
            .filter(item => {
                if (seenCustomers.has(item.customer)) {
                    return false;
                }
                seenCustomers.add(item.customer);
                return true;
            })
            .map((item, index) => ({
                ...item,
                key: item.id,
                SrNo: (page - 1) * pagesize + index + 1,
            }));
    }, [data, page, pagesize]);

    const filteredTotal = useMemo(() => {
        const seenCustomers = new Set();
        return data.filter(item => {
            if (seenCustomers.has(item.customer)) {
                return false;
            }
            seenCustomers.add(item.customer);
            return true;
        }).length;
    }, [data]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const resetFilter = () => {
        setSearch('');
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (current, size) => {
        setPage(1);
        setPageSize(size);
    };

    const GetSortingEnumValue = (sortField, sortOrder) => {
        let sortingEnumKey = 'id';
        if (sortField === 'customer' && sortOrder === 'ascend') {
            sortingEnumKey = 'customer';
        }
        if (sortField === 'customer' && sortOrder === 'descend') {
            sortingEnumKey = '-customer';
        }
        return sortingEnumKey;
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
            setOrderBy(orderBy);
        }
        setPageSize(pagination.pageSize);
    };

    return (
        <>
            <div className="mainwrap">
                <div className="sticyaction">
                    <Row className="pagenamerow" justify="space-between" align="middle">
                        <Col>
                            <h2>Archived Conversion Rates</h2>
                            <div className="bredcrumbwrp">
                                <Link to="/conversion-rate-list" className="back">
                                    <Back /> BACK
                                </Link>
                                <Breadcrumb
                                    items={[
                                        { title: <Link to="/conversion-rate-list">Conversion Rates</Link> },
                                        { title: 'Archived Conversion Rates' },
                                    ]}
                                />
                            </div>
                        </Col>
                        <Col>
                            <Space size="middle">
                                <Input
                                    placeholder="Search"
                                    className="searchbox"
                                    prefix={<SearchOutlined />}
                                    style={{ width: 200 }}
                                    value={search}
                                    onChange={handleSearch}
                                />
                                <Link>
                                    <Tooltip placement="top" title={'Reset Filter'}>
                                        <Button type="primary" disabled={!search} className="iconlink" onClick={resetFilter}>
                                            <FilterIcon />
                                        </Button>
                                    </Tooltip>
                                </Link>
                            </Space>
                        </Col>
                    </Row>
                </div>
                <div className="tablewrp">
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        loading={status === 'loading'}
                        onChange={handleTableChange}
                        scroll={{ x: '600px' }}
                    />
                </div>

                <Pagination
                    current={page}
                    pageSize={pagesize}
                    total={filteredTotal}
                    showSizeChanger
                    onShowSizeChange={handlePageSizeChange}
                    pageSizeOptions={['20', '50', '100']}
                    showTotal={total => `Total ${total} items`}
                    onChange={handlePageChange}
                    locale={{ items_per_page: 'Items / Page' }}
                />

            </div >
        </>
    );
}